import React, { useEffect, useState } from "react";

import { useEmailFeedbackMutation } from "@hooks/useEmailFeedbackMutation";

import { Box } from "@components/common/ResponsiveBox";
import { Modal } from "@components/common/Modal";
import { Text } from "@components/common/Text";
import { TextArea } from "@components/common/TextArea";
import FileInput, { FileStatus } from "@components/common/Input/FileInput";
import AnimatedCheckmark from "./SuccessCheck";

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

interface Props {
  show: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<Props> = ({ show, onClose }) => {
  const [inputValue, setInputValue] = useState("");
  const [files, setFiles] = useState<FileStatus[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  useEffect(() => {
    const formIsEmpty = !inputValue && files.length === 0;
    const filesHaveErrors = files.filter((f) => f.error).length > 0;

    setIsDisabled(formIsEmpty || filesHaveErrors);
  }, [inputValue, files]);

  const [sendEmailFeedback] = useEmailFeedbackMutation((result) => {
    if (result?.error) {
      setStatus("error");
    } else {
      setStatus("success");
    }
  });

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const validate = (file: File) => {
    const isValidExtension = ["png", "jpg", "jpeg"].includes(
      file.type.split("/")[1],
    );

    if (!isValidExtension) {
      return "Invalid file type";
    }

    if (file.size > MAX_FILE_SIZE) {
      return `File is larger than ${MAX_FILE_SIZE_MB} mb`;
    }

    return undefined;
  };

  const handleOnClose = () => {
    setFiles([]);
    onClose();
  };

  const onSubmit = () => {
    setStatus("sending");
    sendEmailFeedback({
      message: inputValue,
      files: files.map((file) => file.file),
    });
  };

  return (
    <Box>
      <Modal
        bodyOverflow
        isOpen={show}
        title={
          status === "error"
            ? "Error"
            : status === "success"
            ? "Thanks for the feedback!"
            : "Contact Us"
        }
        subtitle={
          status === "error"
            ? undefined
            : status === "success"
            ? "Describe your issue or any suggestion you might have"
            : "If needed, we'll contact you shortly"
        }
        onClickCancel={handleOnClose}
        onRequestClose={handleOnClose}
        hasCancel={status !== "success"}
        hasClose={status !== "success"}
        bodyPb="none"
        actions={
          status === "error"
            ? {
                Retry: {
                  disabled: false,
                  onPress: () => setStatus("idle"),
                },
              }
            : status === "success"
            ? {
                Confirm: {
                  disabled: false,
                  onPress: handleOnClose,
                },
              }
            : {
                Send: {
                  disabled: isDisabled,
                  onPress: () => onSubmit(),
                  loading: status === "sending",
                },
              }
        }
      >
        {status === "error" ? (
          <Text paragraph mB="md" mT="xg" size={18} textAlign="center">
            Something went wrong, please try again
          </Text>
        ) : status === "success" ? (
          <AnimatedCheckmark />
        ) : (
          <>
            <Text
              paragraph
              mB="md"
              mT="xg"
              size={12}
              colorWeight={200}
              textAlign="left"
            >
              Please do not include any sensitive information
            </Text>

            <TextArea
              placeholder="Tell us your feedback"
              ariaLabel="Feedback"
              onChange={(e) => handleInputChange(e.target.value)}
              value={inputValue}
            />

            <FileInput
              onChange={(files) => setFiles(files)}
              mT="md"
              validate={validate}
            />
          </>
        )}
      </Modal>
    </Box>
  );
};

export default ContactModal;
